import { transitions as bookingTransitions } from '../transactions/transactionProcessBooking';
import { transitions as offsessionTransitions } from '../transactions/transactionProcessOffsession';
import { timestampToDate } from './dates';

/**
 * Shared booking-date resolution used by the "Upcoming" and "Past" reservation
 * pages so both classify a transaction the same way: Upcoming shows accepted
 * bookings whose date is still in the future, Past shows the same set of
 * transitions once the date has passed (plus completed bookings). Keeping the
 * logic in one place avoids the two pages drifting apart and leaving accepted
 * reservations stranded between them.
 */

/**
 * Transitions that represent a booking the host has accepted and that hasn't been
 * cancelled, declined or expired. Includes Stripe, offsession, and manual-payment flows.
 *
 * `inquire` is included here so that manual-payment bookings (which stay in the inquiry
 * state, see `shouldListInquiry` below) are fetched from the API. The client-side filter
 * then excludes inquire transactions that are NOT active manual reservations.
 *
 * Negative terminal transitions (cancel/decline/expire) are intentionally excluded.
 */
export const VALID_RESERVATION_TRANSITIONS = [
  // Both processes share the same inquire transition value. Manual-payment bookings
  // remain in this state permanently; see `shouldListInquiry` for how they're classified.
  bookingTransitions.INQUIRE,

  // Booking process (Stripe) — accepted onwards
  bookingTransitions.ACCEPT,
  bookingTransitions.OPERATOR_ACCEPT,
  bookingTransitions.COMPLETE,
  bookingTransitions.OPERATOR_COMPLETE,
  bookingTransitions.REVIEW_1_BY_PROVIDER,
  bookingTransitions.REVIEW_2_BY_PROVIDER,
  bookingTransitions.REVIEW_1_BY_CUSTOMER,
  bookingTransitions.REVIEW_2_BY_CUSTOMER,
  bookingTransitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
  bookingTransitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  bookingTransitions.EXPIRE_REVIEW_PERIOD,

  // Offsession process — accepted onwards
  offsessionTransitions.ACCEPT,
  offsessionTransitions.OPERATOR_ACCEPT,
  offsessionTransitions.MARK_PENDING_PAYMENT,
  offsessionTransitions.AUTO_PAYMENT,
  offsessionTransitions.INITIATE_MANUAL_PAYMENT,
  offsessionTransitions.CONFIRM_MANUAL_PAYMENT,
  offsessionTransitions.COMPLETE,
  offsessionTransitions.OPERATOR_COMPLETE,
  offsessionTransitions.REVIEW_1_BY_PROVIDER,
  offsessionTransitions.REVIEW_2_BY_PROVIDER,
  offsessionTransitions.REVIEW_1_BY_CUSTOMER,
  offsessionTransitions.REVIEW_2_BY_CUSTOMER,
  offsessionTransitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
  offsessionTransitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  offsessionTransitions.EXPIRE_REVIEW_PERIOD,
];

export const INQUIRE_TRANSITION = bookingTransitions.INQUIRE;

/** Transitions for reservations awaiting host action (payment confirm / booking request). */
export const PENDING_RESERVATION_TRANSITIONS = [
  bookingTransitions.CONFIRM_PAYMENT,
  offsessionTransitions.REQUEST_BOOKING,
];

export const isPendingReservation = lastTransition =>
  PENDING_RESERVATION_TRANSITIONS.includes(lastTransition);

// ================ Manual / off-platform reservations ================ //

/**
 * "Manual" (off-platform) reservations are payment-link / bank-transfer bookings
 * that stay permanently in the `inquire` state, with all their data living in
 * `metadata`. That metadata has been written by an external system over time and
 * uses several inconsistent schemas, all of which we must recognise:
 *
 *   A) { status: 'accepted', manualPayment: { method, percentPaid, priceOverride, ... } }
 *   B) { estado: 'aceptada', precio_manual: 420 }
 *   C) { estado: 'pending payment', forma_pago: 'Payment link', precio_manual,
 *        porcentaje_pagado, stripe_payment_link_*, stripe_payment_verified_* }
 *
 * The status-bearing field is therefore `estado` (B/C) or `status` (A), and its
 * value is free-form ('accept', 'accepted', 'aceptada', 'pending payment', ...).
 * We classify by normalising that value and excluding only the cancelled/declined
 * ones, rather than allow-listing every spelling of "accepted".
 */

// Keys whose presence marks a transaction as a manual reservation.
const MANUAL_SIGNATURE_KEYS = ['manualPayment', 'precio_manual', 'forma_pago', 'estado'];

// Normalised values of estado/status that mean the reservation is no longer active.
const MANUAL_CANCELLED_STATES = [
  'cancelled',
  'canceled',
  'cancelado',
  'cancelada',
  'declined',
  'rechazada',
  'rechazado',
  'expired',
  'expirada',
  'refunded',
  'reembolsada',
];

// Lower-cased, trimmed, accent-stripped so 'Aceptada' === 'aceptada'.
const normalizeState = value =>
  (value == null ? '' : String(value))
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export const isManualReservation = metadata =>
  !!metadata && MANUAL_SIGNATURE_KEYS.some(key => metadata[key] != null);

/**
 * True when a manual reservation should be listed (Upcoming/Past, by date). A
 * manual booking with no explicit status is treated as active — it's a real
 * booking with a price override — so we only hide the explicitly cancelled ones.
 */
export const isManualReservationActive = metadata => {
  if (!isManualReservation(metadata)) {
    return false;
  }
  const rawState = metadata.estado != null ? metadata.estado : metadata.status;
  if (rawState == null) {
    return true;
  }
  return !MANUAL_CANCELLED_STATES.includes(normalizeState(rawState));
};

/**
 * Whether an `inquire` transaction should appear in the reservation lists.
 * Replaces the old `metadata.status === 'accepted'` check, which only matched
 * one of the three manual-payment metadata schemas in use.
 */
export const shouldListInquiry = metadata => isManualReservationActive(metadata);

// ================ Booking date resolution ================ //

const parseInstant = raw => {
  if (raw == null || raw === '') {
    return null;
  }
  const d = raw instanceof Date ? raw : new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const parseProtectedTs = ts => {
  if (ts == null || ts === '') {
    return null;
  }
  const d = timestampToDate(ts);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Resolves the booking end timestamp from either:
 *  - the `booking` resource included in the API response (Stripe / booking process), or
 *  - `protectedData.bookingEndTime` (offsession, where no `booking` resource is created).
 */
export const resolveBookingEnd = (rawTx, includedBookingsByUuid) => {
  const bookingId = rawTx?.relationships?.booking?.data?.id?.uuid;
  const booking = bookingId ? includedBookingsByUuid[bookingId] : null;
  const ba = booking?.attributes;
  const fromBooking = ba?.displayEnd || ba?.end;
  return (
    parseInstant(fromBooking) || parseProtectedTs(rawTx?.attributes?.protectedData?.bookingEndTime)
  );
};

export const resolveBookingStart = (rawTx, includedBookingsByUuid) => {
  const bookingId = rawTx?.relationships?.booking?.data?.id?.uuid;
  const booking = bookingId ? includedBookingsByUuid[bookingId] : null;
  const ba = booking?.attributes;
  const fromBooking = ba?.displayStart || ba?.start;
  return (
    parseInstant(fromBooking) ||
    parseProtectedTs(rawTx?.attributes?.protectedData?.bookingStartTime)
  );
};

// ================ Fetching ================ //

// The Marketplace API refuses a larger `perPage`.
const MAX_PER_PAGE = 100;

const uniqueByTypeAndId = entities => {
  const seen = new Set();
  return entities.filter(entity => {
    const key = `${entity.type}/${entity.id?.uuid}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/**
 * `sdk.transactions.query` across every page, merged into one response of the
 * same shape. The reservation lists classify by booking date client-side, so a
 * page they skip is a silent hole, not a shorter list.
 *
 * This used to be a single page of 100, and hosts with more matching
 * transactions lost reservations without any sign of it: "Past" asked for
 * `-bookingStart`, which in Sharetribe sorts ASCENDING (the `-` inverts the
 * default descending order), so page 1 held the oldest bookings and the most
 * recent ones sat on a page that was never requested. Transactions with no
 * booking (every inquiry, including manual reservations) sort last in both
 * directions, so they hit the same cap on "Upcoming".
 */
export const queryAllTransactionPages = (sdk, params, queryOpts) => {
  const queryPage = page =>
    sdk.transactions.query({ ...params, perPage: MAX_PER_PAGE, page }, queryOpts);

  return queryPage(1).then(first => {
    const totalPages = first.data.meta?.totalPages || 1;
    const rest = [];
    for (let page = 2; page <= totalPages; page++) {
      rest.push(queryPage(page));
    }
    return Promise.all(rest).then(others => {
      const responses = [first, ...others];
      const concat = key => [].concat(...responses.map(r => r.data[key] || []));
      return {
        ...first,
        data: {
          ...first.data,
          // Deduplicated because a transaction can move between pages while
          // they are being fetched.
          data: uniqueByTypeAndId(concat('data')),
          included: uniqueByTypeAndId(concat('included')),
          meta: { ...first.data.meta, page: 1, totalPages: 1 },
        },
      };
    });
  });
};

export const indexIncludedBookings = response => {
  const map = {};
  (response.data.included || []).forEach(inc => {
    if (inc?.type === 'booking' && inc?.id?.uuid) {
      map[inc.id.uuid] = inc;
    }
  });
  return map;
};

/**
 * Returns true if the booking is still in the future / ongoing.
 *
 * For Sharetribe day/night/hour bookings, `end` is exclusive: a booking ending
 * 2024-05-08T00:00:00Z covers up to 2024-05-07. Comparing `end >= now` with raw
 * timestamps is therefore the right semantics for all unit types — the booking
 * stops being "upcoming" the instant `end` passes.
 *
 * If neither `end` nor `start` are available, the booking is shown (we don't
 * want to hide accepted reservations that lack date metadata for whatever reason).
 */
export const isBookingStillUpcoming = (rawTx, includedBookingsByUuid, now) => {
  const end = resolveBookingEnd(rawTx, includedBookingsByUuid);
  if (end) {
    return end.getTime() >= now.getTime();
  }
  const start = resolveBookingStart(rawTx, includedBookingsByUuid);
  if (start) {
    return start.getTime() >= now.getTime();
  }
  return true;
};

/**
 * Inverse of `isBookingStillUpcoming`. A booking belongs to "Past" once its end
 * (or start, as fallback) has passed. Bookings with no date metadata are NOT
 * treated as past — they stay in "Upcoming" — so the two pages never overlap and
 * never leave a reservation stranded between them.
 */
export const isBookingInPast = (rawTx, includedBookingsByUuid, now) => {
  const end = resolveBookingEnd(rawTx, includedBookingsByUuid);
  if (end) {
    return end.getTime() < now.getTime();
  }
  const start = resolveBookingStart(rawTx, includedBookingsByUuid);
  if (start) {
    return start.getTime() < now.getTime();
  }
  return false;
};
